// DS: C25C
import React, { useState, useEffect, useRef, useCallback, useMemo, } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { BiDataHook } from '@wix/da-bi/pkg/constants';
import UserAvatar from '../User/Avatar/UserAvatar';
import PositionOnlyPopperPopover from '../Popover/Preset/PositionOnlyPopperPopover';
import StandardMenu from '../Dropdown/MenuPreset/StandardMenu';
import { useOnOutsideClick } from '../utils/hooks/useOnOutsideClick';
import CloseBig from '../Icons/CloseBig';
import { IconSize } from '../Icons/IconWrap';
import PlainButtonOrLink from '../Buttons/PlainButtonOrLink';
import VisuallyHidden from '../a11y/VisuallyHidden';
import s from './UserSearchField.scss';
var noop = function () { };
var UserSearchField = function (_a) {
    var placeholder = _a.placeholder, onUserSelect = _a.onUserSelect, onSearch = _a.onSearch, _b = _a.onBlur, onBlur = _b === void 0 ? noop : _b, excludedUsers = _a.excludedUsers, className = _a.className, inputClassName = _a.inputClassName, withClearButton = _a.withClearButton, popupPositionFixed = _a.popupFixedPosition, inputRefFromProps = _a.inputRef, _c = _a.onClear, onClear = _c === void 0 ? noop : _c, onInputKeyDown = _a.onInputKeyDown, _d = _a.usePortal, usePortal = _d === void 0 ? true : _d, isChatContext = _a.isChatContext;
    var t = useTranslation().t;
    var _e = useState(''), inputValue = _e[0], setInputValue = _e[1];
    var _f = useState(0), highlightedMenuItemIndex = _f[0], setHighlightedMenuItemIndex = _f[1];
    var inputRefDefault = useRef(null);
    var rootRef = useRef(null);
    var menuRef = useRef(null);
    var _g = useState([]), matchingUsers = _g[0], setMatchingUsers = _g[1];
    var inputRef = inputRefFromProps || inputRefDefault;
    var dropdownId = useRef(uniqueId('dropdown')).current;
    // focus input on mount
    useEffect(function () {
        inputRef.current && inputRef.current.focus();
    }, [inputRef]);
    var onClearClick = useCallback(function () {
        setInputValue('');
        onClear();
    }, [onClear]);
    var onOutsideClick = useCallback(function () {
        setInputValue('');
        setMatchingUsers([]);
        onBlur();
    }, [onBlur]);
    var onSelect = useCallback(function (user) {
        setInputValue('');
        setMatchingUsers([]);
        onUserSelect(user);
        inputRef.current && inputRef.current.focus();
    }, [onUserSelect, inputRef]);
    useOnOutsideClick([rootRef, menuRef, inputRef], onOutsideClick);
    // set search query
    useEffect(function () {
        if (inputValue.length >= 3) {
            onSearch(inputValue, function (found) {
                if (excludedUsers) {
                    setMatchingUsers(found.filter(function (u) {
                        return !excludedUsers.some(function (u2) {
                            return u2.userId === u.userId || u2.useridUuid === u.useridUuid;
                        });
                    }));
                }
                else {
                    setMatchingUsers(found);
                }
            }, { isChatContext: isChatContext });
        }
        else {
            setMatchingUsers([]);
        }
    }, [onSearch, inputValue, excludedUsers, isChatContext]);
    // arrow keys for menu navigation
    var onRootKeyDown = useCallback(function (e) {
        if (matchingUsers.length === 0) {
            return;
        }
        switch (e.key) {
            case 'ArrowUp':
                setHighlightedMenuItemIndex(highlightedMenuItemIndex === 0
                    ? matchingUsers.length - 1
                    : highlightedMenuItemIndex - 1);
                break;
            case 'ArrowDown':
                setHighlightedMenuItemIndex(highlightedMenuItemIndex === matchingUsers.length - 1
                    ? 0
                    : highlightedMenuItemIndex + 1);
                break;
            case 'Enter':
                if (highlightedMenuItemIndex < matchingUsers.length) {
                    onSelect(matchingUsers[highlightedMenuItemIndex]);
                }
                break;
            default:
                // Don't handle anything else
                break;
        }
    }, [highlightedMenuItemIndex, matchingUsers, onSelect]);
    var menuItems = useMemo(function () {
        return matchingUsers.map(function (user, index) { return ({
            value: user.userId,
            highlighted: index === highlightedMenuItemIndex,
            onMouseEnter: function () { return setHighlightedMenuItemIndex(index); },
            onClick: function () {
                onSelect(user);
            },
            label: (React.createElement(React.Fragment, null,
                React.createElement(UserAvatar, { user: user, size: 26, className: s['autocomplete-avatar'], withLink: false }),
                React.createElement("span", null, user.username))),
        }); });
    }, [highlightedMenuItemIndex, matchingUsers, onSelect]);
    return (React.createElement(PositionOnlyPopperPopover, { className: classnames(s['root'], withClearButton && s['with-clear-button'], className), ref: rootRef, onKeyDown: onRootKeyDown, popoverContent: React.createElement(StandardMenu, { id: dropdownId, ref: menuRef, itemClassName: s['autocomplete-result'], items: menuItems }), placement: "bottom", secondaryPlacement: "start", isVisible: matchingUsers.length > 0, usePortal: usePortal, positionFixed: popupPositionFixed },
        React.createElement("input", { ref: inputRef, type: "text", className: classnames(s['username-input'], inputClassName), value: inputValue, onChange: function (e) { return setInputValue(e.target.value); }, onKeyDown: onInputKeyDown, placeholder: placeholder }),
        withClearButton && (React.createElement(PlainButtonOrLink, { className: s['clear'], onClick: onClearClick, "data-hook": BiDataHook.MANAGE_CHAT_MENU_DELETE_INPUT_TEXT },
            React.createElement(VisuallyHidden, null, t('userSearchField.clear')),
            React.createElement(CloseBig, { size: IconSize.TINY, className: s['clear-icon'] })))));
};
UserSearchField.displayName = 'UserSearchField';
export default UserSearchField;
//# sourceMappingURL=UserSearchField.js.map